import axios from 'axios';

import DeviceProps from '@magnus/react-native-device-props';

import Analytics from '@web-solutions/module-analytics';
import Attribution from '@web-solutions/module-attribution';

/**
 * Attaches the captured FB ad labels to the two user-API calls that carry them:
 *
 *  - `POST /v2/user`                 — user creation, the earliest point there is
 *  - `PUT /user/update-by-token/:id` — every quiz save, the main working path
 *
 * The backend stamps the FIRST snapshot it ever receives and never overwrites,
 * so re-sending on every call is deliberate — there is nothing to deduplicate.
 *
 * Done as an interceptor rather than at each call site: between them these two
 * requests are fired from ~20 pages and hooks (every personal-plan variant, the
 * paywall, the email popups, the anonymous-user hook), and a call that quietly
 * misses the labels stays invisible until the cohort report is already wrong.
 *
 * This is also the only channel that reaches the backend for EVERY payment
 * system on this brand: the TailoredPay purchase itself goes through /nmi/*
 * (which carries the labels too), but the user record is created here first.
 */
const UPDATE_BY_TOKEN_PATH = '/user/update-by-token/';
const CREATE_USER_PATH = '/v2/user';

/**
 * Capture at module-import time, not from a React render.
 *
 * The entry file imports this module as its second side effect, so the URL is
 * read before the store hydrates, before the first render and before any hook
 * can fire a request. `Attribution.init()` also captures, but it runs inside the
 * `Root` component body — late enough that an early `POST /v2/user` could race
 * it and go out with no labels.
 */
Attribution.captureAdAttribution();

/**
 * `idfm` on user creation is the second key that ties a purchase to a user —
 * the backend asked for it on every brand. Same value the funnel already sends
 * to the payment endpoints; here it is worth sending even for an organic visit.
 */
let idfmPromise: Promise<string | undefined> | null = null;

function getIdfm(): Promise<string | undefined> {
  if (!idfmPromise) {
    idfmPromise = DeviceProps.getIDFM().catch(() => undefined);
  }

  return idfmPromise;
}

type AttributedRequest = 'create_user' | 'update_by_token';

// One report per request kind per page load: update-by-token fires on every
// quiz step, and an organic visit would otherwise emit dozens of identical events.
const missingReported: Partial<Record<AttributedRequest, true>> = {};

/**
 * A request that carries the labels reached the backend without them. Says
 * which request and, as far as the client can tell, why — so the "why is half
 * of attribution missing" question is a PostHog query, not a week of guessing.
 *
 * `reason` is derived from the module's own health check unless the caller knows
 * better (an unusable body). Every value is a string: an `undefined` property
 * never enters the PostHog taxonomy and reads as "never implemented".
 */
function reportAttributionMissing(request: AttributedRequest, reasonOverride?: string): void {
  if (missingReported[request]) {
    return;
  }
  missingReported[request] = true;

  try {
    const diag = Attribution.getAdAttributionDiag();

    let reason = reasonOverride;

    if (!reason) {
      if (diag.source !== 'none') {
        // A snapshot exists but has neither campaignId nor adId — withheld on
        // purpose, the backend would discard it and burn the first-touch slot.
        reason = 'only_fbclid_or_source';
      } else if (diag.urlParams === 'yes') {
        // This load's URL had labels and nothing can be read back. Memory should
        // make this impossible — if it ever fires, something new is going on.
        reason = 'captured_but_unreadable';
      } else if (diag.storage === 'blocked') {
        // No labels in this URL and the store refused to write: most likely a
        // reload/return after a landing whose labels could not be persisted.
        reason = 'no_url_params_storage_blocked';
      } else {
        reason = 'no_url_params';
      }
    }

    Analytics.trackEvent('attribution', 'missing', {
      request,
      reason,
      storage: diag.storage,
      source: diag.source,
      url_params: diag.urlParams,
    });
  } catch (_) {
    // Diagnostics must never break the request they describe.
  }
}

let registered = false;

export function registerAdAttributionInterceptor(): void {
  // StrictMode renders the root twice — a second interceptor would be harmless
  // but pointless.
  if (registered) {
    return;
  }
  registered = true;

  axios.interceptors.request.use(async (config) => {
    try {
      const url = config.url || '';
      const method = config.method?.toLowerCase();

      const isCreateUser = method === 'post' && url.includes(CREATE_USER_PATH);
      const isUpdateByToken = method === 'put' && url.includes(UPDATE_BY_TOKEN_PATH);

      if (!isCreateUser && !isUpdateByToken) {
        return config;
      }

      const request: AttributedRequest = isCreateUser ? 'create_user' : 'update_by_token';
      const attribution = Attribution.getAdAttribution();

      if (!attribution) {
        reportAttributionMissing(request);
      }

      // `idfm` goes on user creation regardless of attribution — it is the join
      // key for the purchase, not part of the labels.
      const idfm = isCreateUser ? await getIdfm() : undefined;

      if (!attribution && !idfm) {
        return config;
      }

      // `POST /v2/user` is sometimes called with no body at all (the anonymous
      // user hook), so there is nothing to spread — start from an empty object.
      const data = config.data ?? (isCreateUser ? {} : undefined);

      if (!data || typeof data !== 'object' || data instanceof FormData) {
        if (attribution) {
          reportAttributionMissing(request, 'body_not_json');
        }
        return config;
      }

      config.data = {
        ...data,
        // Never override an idfm a call site set itself.
        ...(idfm && !(data as any).idfm ? { idfm } : {}),
        // Organic visit — no `attribution` key at all, not an empty object.
        ...(attribution ? { attribution } : {}),
      };
    } catch (_) {
      // Attribution must never break creating the user or saving the questionnaire.
    }

    return config;
  });
}

registerAdAttributionInterceptor();
